import { Form, Formik } from 'formik';
import React, { useContext, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import SaveBackdrop from '../../components/saveBackdrop';
import { AppContext } from '../../contexts/app-context';
import GlobalStyles from '../../global-styles';
import API from '../../utils/api';
import {
  apiReponseError, apiRequestError, canReadEnrollmentFile, checkUserAuthenticated, getErrorObject,
  goToRoutePage
} from '../../utils/helper';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, InputLabel,
  TextField, Typography
} from '../../utils/material-ui-core';

interface UploadEnrollmentFileProps {
  open: boolean;
  onCallback: (confirmOutput: ConfirmOutput) => void;
}

const formDefaultValue = {
  file: "",
};

const formDefaultTouchValue = {
  file: true,
};

// Form validation schema
const formValidationSchema = Yup.object().shape({
  file: Yup.string().trim().required("Enrollment file is required"),
});

const UploadEnrollmentFile = ({ open, onCallback }: UploadEnrollmentFileProps) => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const { isUserAuthenticated, userFunctionAccess, showAlert } = useContext(AppContext);

  const formReference = useRef(null) as any;

  const [initialFormValues, setInitialFormValues] = useState(formDefaultValue);
  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);
  const [fileEvent, setFileEvent] = useState<any>();

  // Upload file for validation
  const uploadFile = async () => {
    formReference.current.validateForm();

    if (!formReference.current.isValid) {
      return;
    }

    if (fileEvent !== null) {
      setSaveInProgress(true);

      const payload = new FormData();
      payload.append("file", fileEvent.target.files[0]);

      try {
        await API.postfile("/api/enrollment/batch/files/upload", payload)
          .then(async (response) => {
            showAlert({ message: "Successfully saved.", severity: "success" });
            reset();
            refresh();
            return;
          })
          .catch((error) => {
            setSaveInProgress(false);
            const errorList: ApiResponseErrorObject[] = getErrorObject(error, "program.");
            let isErrorAssignedAtFieldLevel = true;
            try {
              errorList.forEach(function (error) {
                formReference.current.setFieldError(error.title, error.message.join(" "));
              });
            } catch (e) {
              isErrorAssignedAtFieldLevel = false;
            }
            isErrorAssignedAtFieldLevel = false;
            if (errorList.length <= 0 || !isErrorAssignedAtFieldLevel) {
              showAlert({
                message: apiReponseError(error, "Failed to upload file."),
                severity: "error",
              });
            }
          });
      } catch (e) {
        setSaveInProgress(false);
        showAlert({
          message: apiRequestError(e, "Something went wrong in our system."),
          severity: "error",
        });
      }
    }
  };

  // Reset input file control and other variables
  const reset = () => {
    setSaveInProgress(false);
    setInitialFormValues(formDefaultValue);
  };

  // Cancel upload file
  const refresh = () => {
    onCallback({ status: "refresh", data: null });
  };

  // Cancel upload file
  const cancel = () => {
    onCallback({ status: "cancel", data: null });
  };

  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open]);

  useEffect(() => {
    const initializePage = async () => {
      checkUserAuthenticated(isUserAuthenticated);

      if (!canReadEnrollmentFile(userFunctionAccess)) {
        showAlert({ message: "Access Denied.", severity: "error" });
        goToRoutePage("home");
        return;
      }
    };
    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthenticated]);

  return (
    <Dialog className={globalStyles.dialogContainer} fullWidth={true} open={open} maxWidth={"md"}>
      <DialogTitle className={globalStyles.dialogTitleContainer}>
        <Typography variant="subtitle1" component="div">
          Enrollment File Upload
        </Typography>
      </DialogTitle>

      <DialogContent dividers className={globalStyles.formikContainer}>
        <SaveBackdrop open={saveInProgress} />

        <Box className={pageStyles.root}>
          <Formik innerRef={formReference} initialValues={initialFormValues} validationSchema={formValidationSchema} validateOnMount={true} initialTouched={formDefaultTouchValue} validateOnBlur={true} validateOnChange={true} onSubmit={(values) => { }}>
            {({ values, errors, handleChange, handleSubmit, touched, handleBlur, isSubmitting, setFieldValue }) => (
              <Form>
                <Grid container spacing={3} className={globalStyles.bottomSpacer}>
                  <Grid item xs={12}>
                    <FormControl className={globalStyles.formControl} fullWidth={true}>
                      <InputLabel shrink={true}>
                        Enrollment file &nbsp;&nbsp;
                        <small>(CSV (tilde delimited); or Excel (xlsx)).</small>
                      </InputLabel>
                      <br />
                      <TextField
                        name="file"
                        type="file"
                        id="file"
                        value={values.file}
                        onChange={(event) => {
                          handleChange(event);
                          setFileEvent(event);
                        }}
                        onBlur={handleBlur}
                        error={Boolean(errors.file)}
                        helperText={errors.file ? errors.file : null}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button color="secondary" variant="contained" onClick={uploadFile} className={globalStyles.buttonDefault}>
          Upload
        </Button>
        &nbsp;
        <Button onClick={() => cancel()} color="secondary" autoFocus className={globalStyles.buttonDefault}>
          <u>Cancel</u>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: 0,
    },
    errormessage: {
      color: theme.palette.error.main,
    },
  })
);

export default UploadEnrollmentFile;
